/**
 * Helpers shared by the full guest-invitation form (`GuestInvitationForm`)
 * and the home "Invite a guest" sheet, so both validate and normalise input
 * the same way.
 */
import { formatPhoneAsYouType, toE164 } from '@hub-wifi/shared'
import { ApiError } from '../api/client'
import { addDaysInTz, formatHourMinute, nowHmInTz, todayYmdInTz } from './calendar-time'

export interface VisitWindow {
  visitDate: string
  visitTimeBegin: string
  visitTimeEnd: string
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Default business-hours window for a new invitation.
export const VISIT_OPEN_TIME = '09:00'
export const VISIT_CLOSE_TIME = '18:00'

/** Weekday (0=Sun..6=Sat) of a YYYY-MM-DD, anchored at noon UTC (DST-safe). */
function weekdayOfYmd(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()
}

/** Next calendar day after `ymd` that is not Saturday or Sunday. */
function nextWorkdayYmd(ymd: string, tz: string): string {
  let next = addDaysInTz(ymd, 1, tz)
  while (weekdayOfYmd(next) === 0 || weekdayOfYmd(next) === 6) {
    next = addDaysInTz(next, 1, tz)
  }
  return next
}

/**
 * Default visit date + begin/end in the BUILDING's timezone (the frame the API
 * validates against).
 *
 * - Outside business hours (before 08:00 or at/after 20:00): next workweek day
 *   (skip Sat/Sun), full OPEN..CLOSE window.
 * - During business hours: today, begin = now to the minute (never before
 *   OPEN). If that leaves no room before CLOSE, roll to the next workday.
 */
export function defaultVisitWindow(tz: string, now: Date = new Date()): VisitWindow {
  const today = todayYmdInTz(tz, now)
  const nowHm = nowHmInTz(tz, now)
  const hour = Number(nowHm.slice(0, 2))
  const nextDay = {
    visitDate: nextWorkdayYmd(today, tz),
    visitTimeBegin: VISIT_OPEN_TIME,
    visitTimeEnd: VISIT_CLOSE_TIME,
  }
  if (hour < 8 || hour >= 20) return nextDay
  const begin = nowHm < VISIT_OPEN_TIME ? VISIT_OPEN_TIME : nowHm
  if (begin >= VISIT_CLOSE_TIME) return nextDay
  return { visitDate: today, visitTimeBegin: begin, visitTimeEnd: VISIT_CLOSE_TIME }
}

export interface VisitWindowCheck {
  /** i18n key of a blocking error, or `null`. */
  errorKey: string | null
  /** A same-day begin that already passed: not an error, the API clamps it to now. */
  beginsNow: boolean
  todayYmd: string
  nowHm: string
}

/**
 * Time guards evaluated in the building timezone so they agree with the
 * server. A same-day begin in the past is clamped by the API (only noted);
 * an end in the past can't be clamped into anything, so it blocks.
 */
export function checkVisitWindow(form: VisitWindow, tz: string, now: Date): VisitWindowCheck {
  const todayYmd = todayYmdInTz(tz, now)
  const nowHm = nowHmInTz(tz, now)
  const shapeError = visitWindowShapeError(form)
  const isToday = form.visitDate === todayYmd
  const dateInPast = form.visitDate !== '' && form.visitDate < todayYmd
  const beginInPast = isToday && form.visitTimeBegin !== '' && form.visitTimeBegin < nowHm
  const endInPast = isToday && form.visitTimeEnd !== '' && form.visitTimeEnd <= nowHm
  const errorKey =
    shapeError === 'guestInvitations.form.dateTimeRequired'
      ? shapeError
      : dateInPast
        ? 'guestInvitations.form.dateInPast'
        : endInPast
          ? 'guestInvitations.form.endInPast'
          : shapeError
  return { errorKey, beginsNow: beginInPast && errorKey === null, todayYmd, nowHm }
}

/**
 * Checks that need no clock or timezone: every field filled in, and begin
 * before end. Usable before the building (timezone) has loaded.
 */
export function visitWindowShapeError(form: VisitWindow): string | null {
  if (form.visitDate === '' || form.visitTimeBegin === '' || form.visitTimeEnd === '') {
    return 'guestInvitations.form.dateTimeRequired'
  }
  if (form.visitTimeBegin >= form.visitTimeEnd) return 'guestInvitations.form.rangeInvalid'
  return null
}

/**
 * Stored phones are E.164 (`+5511914725295`); the field shows them through the
 * same as-you-type formatter used while editing, mirroring
 * CoworkerEditForm#formatStoredPhoneForDisplay.
 */
export function formatStoredPhoneForDisplay(phone: string | null): string {
  if (!phone) return ''
  return formatPhoneAsYouType(phone, 'BR') || phone
}

/** Live BR mask; formatting is skipped while deleting so backspace can eat
 *  separators instead of the formatter re-inserting them. */
export function maskGuestPhoneInput(previous: string, raw: string): string {
  const deleting = raw.length < previous.length
  return deleting ? raw : formatPhoneAsYouType(raw, 'BR') || raw
}

/**
 * Normalise the masked phone to E.164 so the API (and the stored row) never
 * sees the display format. Empty input is fine (`null`); unparseable input
 * returns `{ ok: false }` so the form can reject it inline instead of
 * round-tripping to a 400.
 */
export function normalizeGuestPhone(
  input: string,
): { ok: true; e164: string | null } | { ok: false } {
  const trimmed = input.trim()
  if (trimmed === '') return { ok: true, e164: null }
  const e164 = toE164(trimmed, 'BR')
  return e164 ? { ok: true, e164 } : { ok: false }
}

/** User-facing message for a failed `createGuestInvitation`. */
export function describeGuestInvitationError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const code = (err.body as { code?: string } | undefined)?.code ?? 'TRUESAFE_UNAVAILABLE'
    return t(`guestInvitations.errors.${code}`) || err.message
  }
  return t('common.error')
}

/** "2026-05-13T23:00:00-03:00" → "2026-05-13". Booking DTOs carry the
 *  BUILDING's offset, so the wall-clock part is already building-local;
 *  `new Date()` would re-render it in the browser's timezone. */
export function isoWallClockDatePart(iso: string): string {
  return iso.slice(0, 10)
}
/** "2026-05-13T23:00:00-03:00" → "23:00". See `isoWallClockDatePart`. */
export function isoWallClockTimePart(iso: string): string {
  return iso.slice(11, 16)
}

/** '2026-04-30' → '30/04/2026' for confirmation lines. */
export function ymdToDayFirst(ymd: string): string {
  const [y, m, d] = ymd.split('-')
  return `${d}/${m}/${y}`
}

/** Building-local visit window of a booking (the server re-derives it anyway). */
export function bookingWindow(
  booking: { startAt: string; endAt: string },
  timeZone: string,
): VisitWindow {
  return {
    visitDate: todayYmdInTz(timeZone, new Date(booking.startAt)),
    visitTimeBegin: formatHourMinute(booking.startAt, timeZone),
    visitTimeEnd: formatHourMinute(booking.endAt, timeZone),
  }
}
